import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import { Toggler } from '~/platform/utilities/feature-toggles';
import { SUBMIT_OTHER_FORM_LIST } from '~/platform/site-wide/representative/utilities/constants';
import { HELP_BC_LABEL, HelpBC } from '../utilities/poaRequests';

const VAGovLink = () => (
  <>
    &nbsp;
    <Link className="content-link" to="https://va.gov">
      VA.gov
    </Link>
  </>
);
const EmailHelpLink = () => (
  <Link to="mailto:RepresentativePortalHelp@va.gov" className="content-link">
    RepresentativePortalHelp@va.gov
  </Link>
);
const RepresentationLink = (additionalText = '') => (
  <Link
    to="https://www.va.gov/get-help-from-accredited-representative/appoint-rep/introduction/"
    className="content-link"
  >
    VA Form 21-22
    {additionalText}
  </Link>
);

const HelpPage = title => {
  useEffect(() => {
    document.title = title.title;
  }, [title]);
  const { TOGGLE_NAMES } = useFeatureToggle();

  return (
    <>
      <VaBreadcrumbs
        breadcrumbList={HelpBC}
        label={HELP_BC_LABEL}
        homeVeteransAffairs={false}
        wrapping
      />
      <article className="help">
        <h1 id="get-help-page-heading" data-testid="get-help-page-heading">
          Get help with the Accredited Representative Portal
        </h1>

        <div className="vads-l-row">
          <div className="vads-l-col--12 medium-screen:vads-l-col--8 left-section">
            <p className="subtext">
              Learn about current and future features of the portal and how to
              get started using the portal. Get help resolving common issues
              with sign in.
            </p>
            <va-on-this-page />

            <h2 id="overview-of-the-portal">Overview of the portal</h2>

            <p>
              The Accredited Representative Portal is a secure online tool for
              accredited representatives to efficiently establish representation
              and manage their work supporting Veterans and dependents through
              the benefits claims process.
            </p>

            <va-accordion>
              <va-accordion-item
                header="Current and upcoming features"
                id="section-one"
                level="3"
              >
                The portal opens to all accredited representatives in Fall 2025.
                At that time, representatives will be able to complete these
                tasks:
                <ul>
                  <li>
                    Establish representation by accepting requests that were
                    submitted using online {RepresentationLink()} on VA.gov.
                    Access the VBMS eFolder within minutes of establishing
                    representation.
                  </li>
                  <li>
                    Search for claimants to verify whether you or your
                    organization represents them.
                  </li>
                  <li>
                    Upload and submit completed PDFs and evidence for VA Forms
                    21-686c and 21-526EZ.
                  </li>
                </ul>
                We will expand the portal’s capabilities over time, starting
                with these tasks:
                <ul>
                  <li>Access the portal with the ID.me sign-in method</li>
                  <li>Submit additional types of VA forms</li>
                  <li>View summary information about a claimant</li>
                  <li>
                    Complete and submit the Application for Accreditation as a
                    Claims Agent or Attorney (VA Form 21a)
                  </li>
                </ul>
              </va-accordion-item>
              <va-accordion-item
                header="How does the portal prevent fraud and ensure security?"
                id="section-two"
                level="3"
              >
                <p>
                  Accredited representatives must sign in to the portal with a
                  VA-approved sign-in account. During sign-in, the system checks
                  with the VA’s Office of General Counsel (OGC) to confirm
                  current accreditation. If the system can’t verify current
                  accreditation, we restrict access to portal features. These
                  identity checks make fraud very difficult.
                </p>
                <p>
                  To submit {RepresentationLink()} on VA.gov, Veterans must sign
                  in to their {VAGovLink()} account, which requires
                  authentication with ID.me or Login.gov. By requiring sign-in
                  with these methods, we ensure that all submitters have
                  verified their identity with the federal government.
                </p>
              </va-accordion-item>
            </va-accordion>
            <h2 id="creating-your-account">Creating your account</h2>
            <p>
              To use the Accredited Representative Portal, you need to create an
              ID.me or a Login.gov account and associate it with your email on
              file with the VA’s Office of General Counsel (OGC).
            </p>
            <va-accordion>
              <va-accordion-item
                header="Setting up your account"
                id="section-three"
                level="3"
              >
                <h4>Step 1: Identify the email you have on file with OGC</h4>
                <p>
                  If you’re unsure which email you have on file with OGC, look
                  yourself up using the{' '}
                  <Link
                    className="content-link"
                    to="https://www.va.gov/get-help-from-accredited-representative/find-rep/"
                  >
                    Find a Representative tool (VA.gov)
                  </Link>
                  . If you have an email on file with OGC, it will be displayed
                  there.
                  <br />
                  <br />
                  If you aren’t able to find yourself using the Find a
                  Representative tool, try using the{' '}
                  <Link
                    className="content-link"
                    to="https://www.va.gov/ogc/apps/accreditation/index.asp"
                  >
                    OGC accreditation search tool
                  </Link>
                  . If you are found in the OGC tool but not the Find a
                  Representative tool, it likely means VA doesn’t have a valid
                  physical address on file for you.
                </p>
                <h4>Step 2: Create an ID.me or Login.gov account</h4>
                <p>
                  ID.me and Login.gov are services that provide secure ways to
                  sign in to many government websites using just one account.
                  When creating your account, it’s recommended to use a personal
                  email you’ll always have access to. Follow the service
                  provider’s instructions to create your account.
                </p>
                <Link
                  className="content-link"
                  to="https://login.gov/help/create-account/how-do-i-create-an-account/"
                >
                  Learn how to create your Login.gov account
                </Link>
                <br />
                <Link
                  className="content-link"
                  to="https://help.id.me/hc/en-us/articles/202673924-Create-your-ID-me-account"
                >
                  Learn how to create your ID.me account
                </Link>
                <h4>Step 3: Associate your OGC email with your account</h4>
                <p>
                  If you created your ID.me or Login.gov account using your
                  personal email, you’ll need to add the email address you have
                  on file with OGC. Follow the service provider’s instructions
                  to add an email to your account.
                </p>
                <Link
                  className="content-link"
                  to="https://www.login.gov/help/manage-your-account/change-your-email-address/"
                >
                  Learn how to add an email to your Login.gov account
                </Link>
                <br />
                <Link
                  className="content-link"
                  to="https://help.id.me/hc/en-us/articles/19679310213271-Add-a-work-or-business-email-to-your-ID-me-account"
                >
                  Learn how to add an email to your ID.me account
                </Link>
                <h4>Step 4: Sign in to the portal</h4>
                <p>
                  Sign in to the portal at va.gov/representative . If you
                  receive a message saying that we can’t verify you’re an
                  accredited representative, refer to the commonly asked
                  questions in this section for next steps.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="What if I'm having trouble creating my account?"
                id="section-four"
                level="3"
              >
                <p>
                  We can help guide you to fix some common issues as you create
                  your account and verify your identity.
                </p>
                <Link
                  className="content-link"
                  to="https://www.va.gov/resources/support-for-common-logingov-and-idme-issues/"
                >
                  Get support for common ID.me and Login.gov issues
                </Link>
                <br />
                <br />
                <p>
                  Or you can get more help on each account provider’s website.
                </p>
                <p>
                  <Link
                    className="content-link"
                    to="https://help.id.me/hc/en-us"
                  >
                    Go to the ID.me support section
                  </Link>
                  <br />
                  <Link className="content-link" to="https://login.gov/help/">
                    Go to the Login.gov help center
                  </Link>
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="What if I don’t have an email on file with OGC, or the email is incorrect?"
                id="section-six"
                level="3"
              >
                <p>
                  <strong>
                    If you’re a Veteran Service Organization (VSO)
                    representative
                  </strong>
                  , reach out to the certifying official at your VSO who handles
                  the accreditation process. Ask them to request that OGC add an
                  email to your file or update your existing email. If your
                  contact information or physical address is outdated, request
                  that OGC update it as well. We recommend against using a PO
                  Box, since this will prevent your record from appearing in the
                  Find a Representative tool.
                </p>
                <p>
                  If you’re unsure who your certifying official is, contact your
                  supervisor for guidance. Once the OGC update is complete, add
                  the updated email address to your Login.gov account and try
                  signing in to the portal.
                </p>
                <p>
                  <strong>
                    If you’re an accredited attorney or claims agent
                  </strong>
                  , follow the instructions on the PDF fact sheet,{' '}
                  <Link
                    className="content-link"
                    to="https://www.va.gov/OGC/docs/Accred/FactSheet_OBI-21-03.pdf"
                  >
                    Process for Attorney and Claims Agent Contact Change
                    Requests
                  </Link>
                  , found on the{' '}
                  <Link
                    className="content-link"
                    to="https://www.va.gov/ogc/accreditation.asp"
                  >
                    OGC Accreditation webpage
                  </Link>
                  .
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="What if I’ve associated my OGC email and still can’t sign in to the portal?"
                id="section-eight"
                level="3"
              >
                <p>
                  Make sure the email address you have on file with OGC is
                  unique to you. If you’re using a shared organization email
                  that other representatives also use, the portal won’t grant
                  you access. In that case, you’ll need to update your OGC email
                  to one that only you use.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="What if I’ve tried everything and still can’t sign in?"
                id="section-seven"
                level="3"
              >
                <p>
                  If you’re still unable to resolve the issue, call the VA
                  accredited representative support line at{' '}
                  <va-telephone contact="8552250709" />.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="Can I sign in with my PIV?"
                id="section-ten"
                level="3"
              >
                <p>Yes, but only if you’re using Login.gov.</p>
                <p>
                  After creating your Login.gov account and associating it with
                  your email on file with OGC, you can add your PIV card as an
                  authentication method. After you add your PIV card to your
                  account, select the "Sign in with your government ID" option
                  the next time you sign in.
                </p>
                <p>
                  <Link
                    className="content-link"
                    to="https://www.login.gov/help/create-account/authentication-methods/piv-cac/"
                  >
                    Learn how to set up your PIV card on an existing Login.gov
                    account
                  </Link>
                </p>
                <p>
                  <strong>Note:</strong> We are exploring direct sign-in with
                  PIV as a future enhancement.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="I'm also a Veteran. Will my Veteran benefits be impacted by my usage of the portal?"
                id="section-eleven"
                level="3"
              >
                <p>
                  No, the benefits you receive as a Veteran won’t be affected by
                  using the portal.
                </p>

                <p>
                  VA.gov uses your Veteran profile, while the portal uses a
                  separate representative profile. There is no co-mingling of
                  information between the two systems.
                </p>
              </va-accordion-item>
            </va-accordion>
            <h2 id="establishing-representation">
              Establishing representation
            </h2>
            <p>
              You can quickly establish representation (power of attorney) using
              the Representation Requests feature in the portal. Within minutes
              of accepting a request, you’ll have access to the claimant’s
              eFolder in VBMS.
            </p>
            <va-accordion>
              <va-accordion-item
                header="Gaining access to the Representation Requests feature"
                id="section-twelve"
                level="3"
              >
                <p>
                  Veterans Service Organizations (VSOs) have to activate the
                  Representation Requests feature for their organization if they
                  want to receive requests in the portal. If you’d like your VSO
                  to activate this feature, ask the VSO manager or certifying
                  official to email us at {EmailHelpLink()}.
                </p>
                <p>
                  <strong>Note:</strong> This feature is currently not available
                  to claims agents or attorneys. It may become available to them
                  in the future.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="Receiving requests in the portal"
                id="section-thirteen"
                level="3"
              >
                <p>Here’s how to receive requests in the portal:</p>

                <ul>
                  <li>
                    The Veterans Service Organization (VSO) has to activate the
                    Representation Requests feature for their organization,{' '}
                    <strong>and</strong>
                  </li>

                  <li>
                    Claimants need to submit requests using the online{' '}
                    {RepresentationLink(' (on VA.gov)')}
                  </li>
                </ul>

                <p>
                  Claimants have to be signed in to VA.gov to submit the online
                  form. They’ll need to select you as the accredited
                  representative, and appoint one of your organizations that has
                  activated the Representation Requests feature in the portal.
                  Once the claimant submits the online form, their
                  representation request will be received in the portal.
                </p>
                <p>
                  Representation requests expire after 60 days. Expired requests
                  are removed from the portal for data security purposes.
                </p>
                <p>
                  <strong>Note:</strong> The portal doesn’t receive requests
                  submitted by mail, fax, QuickSubmit, or other tools. Also, the
                  portal is the only tool that receives requests that are
                  submitted using online {RepresentationLink(' (on VA.gov)')}.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="Reviewing, accepting, and declining requests"
                id="section-fourteen"
                level="3"
              >
                <p>
                  The portal shows a list of requests for your Veterans Service
                  Organization (VSO) that have been received in the portal over
                  the last 60 days. The VSO controls whether you can review
                  details for all of their requests, or only the requests where
                  you are the preferred representative.
                </p>

                <p>
                  Select a request from the list to review its details. The
                  request details will show additional claimant information, and
                  authorization information about protected medical records and
                  change of address. After reviewing the request details, there
                  is an option to accept or decline the request. Follow your
                  organization’s policy about which requests you can accept or
                  decline.
                </p>
                <p>
                  Once a request is accepted and processed, you’ll have access
                  to the claimant’s eFolder in VBMS. The portal will also send
                  an email to the claimant letting them know your decision to
                  accept or decline the request.
                </p>
              </va-accordion-item>
            </va-accordion>
            <h2 id="searching-for-claimants-you-represent">
              Searching for claimants you represent
            </h2>
            <p>
              You can find claimants who have recently requested representation
              or who have existing representation with you or one of your VSOs.
            </p>
            <va-accordion>
              <va-accordion-item
                header="How to search using the tool"
                id="section-fifteen"
                level="3"
              >
                To search for a claimant, you’ll need to enter their first name,
                last name, date of birth, and Social Security number. Even if
                you enter the information correctly, you’ll receive a "No result
                found" message if the individual doesn’t have a recent
                representation request or existing representation with you or
                one of your VSOs.
              </va-accordion-item>
              <va-accordion-item
                header="Does the search function check for limited representation?"
                id="section-sixteen"
                level="3"
              >
                <p>
                  The portal doesn’t currently check for limited representation.
                  By limited representation, we mean that the representation is
                  for a specific claim or claims. Check with the claimant or in
                  VBMS for any existing limited representation.
                </p>
                <p>
                  We are exploring search for limited representation as a future
                  enhancement.
                </p>
              </va-accordion-item>
            </va-accordion>
            <h2 id="submitting-va-forms">Submitting VA forms</h2>
            <Toggler
              toggleName={
                TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateQsLink
              }
            >
              <Toggler.Enabled>
                <p>
                  Use the Submissions feature to submit forms and files for
                  claimants you represent. For fastest processing, fill out and
                  submit online forms. For all other forms, you will need a
                  completed PDF to submit.
                </p>
                <p>
                  <va-link
                    href="https://www.youtube.com/watch?v=xgYrov0LKp8"
                    text="Play a video of how to submit VA Form 21-686c"
                  />
                </p>
                <p>
                  <va-link
                    href="https://www.youtube.com/watch?v=a3VijAaWHM4"
                    text="Play a video of how to submit VA Form 21-526EZ"
                  />
                </p>
                <p>
                  <va-link
                    href="/representative/help/quicksubmit"
                    text="Get help using QuickSubmit in the portal"
                  />
                </p>
              </Toggler.Enabled>
              <Toggler.Disabled>
                <p>
                  Use the Submissions feature to submit VA forms for claimants.
                  The portal will verify that you or your Veterans Service
                  Organization (VSO) represent the claimant before you can
                  submit a form on their behalf. You won’t be able to submit the
                  form if you don’t represent them.
                </p>
                <p>
                  Some forms require you to upload a completed PDF (no larger
                  than 25MB) to submit. Other forms can be submitted by simply
                  filling out the required steps in the portal.
                </p>
                <p>
                  <va-link
                    href="https://www.youtube.com/watch?v=a3VijAaWHM4"
                    text="Play a video of how to submit VA Form 21-526EZ"
                  />
                </p>
                <p>
                  <va-link
                    href="https://www.youtube.com/watch?v=xgYrov0LKp8"
                    text="Play a video of how to submit VA Form 21-686c"
                  />
                </p>
              </Toggler.Disabled>
            </Toggler>
            <va-accordion>
              <va-accordion-item
                header="VA Form 21-0966 (Intent to File a Claim for Compensation and/or Pension, or Survivors Pension and/or DIC)"
                id="section-seventeen"
                level="3"
              >
                <p>
                  Fill out the required steps in the portal for VA Form 21-0966,
                  and then submit.
                </p>
                <p>
                  The intent to file will be recorded immediately after
                  submission.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="VA Form 21-526EZ (Application for Disability Compensation and Related Compensation Benefits)"
                id="section-eighteen"
                level="3"
              >
                <p>
                  Upload the completed VA Form 21-526EZ PDF (no larger than
                  25MB) and any supporting evidence, and then submit.
                </p>
                <p>
                  The form will be processed by VA Centralized Mail after
                  submission. We’ll notify you of the status of receipt in VBMS.
                  The portal will track the submission for 60 days.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="VA Form 21-686c (Application Request to Add and/or Remove Dependents)"
                id="section-nineteen"
                level="3"
              >
                <p>
                  Upload the completed VA Form 21-686c PDF (no larger than 25MB)
                  and any supporting evidence, and then submit.
                </p>
                <p>
                  The form will be processed by VA Centralized Mail after
                  submission. We’ll notify you of the status of receipt in VBMS.
                  The portal will track the submission for 60 days.
                </p>
                <p>
                  <strong>Note:</strong> The portal isn’t able to establish
                  dependents within 24 hours at this time. We’re exploring ways
                  to speed up this process as a future enhancement.
                </p>
              </va-accordion-item>
              <va-accordion-item
                header="Submit other forms"
                id="section-nineteen"
                level="3"
              >
                <p>
                  You can submit a number of other VA forms using the portal.
                  Upload the completed VA form PDF (no larger than 25MB) and any
                  supporting evidence, and then submit.
                </p>
                <p>
                  The form will be processed by VA Centralized Mail after
                  submission. We’ll notify you of the status of receipt in VBMS.
                  The portal will track the submission for 60 days.
                </p>
                <va-additional-info trigger="Check which forms can be submitted">
                  <ul>
                    {Object.entries(SUBMIT_OTHER_FORM_LIST).map(form => (
                      <li key={form[0]}>
                        {`${form[0]} - ${form[1].subTitle}`}
                      </li>
                    ))}
                  </ul>
                </va-additional-info>
              </va-accordion-item>
              <Toggler
                toggleName={
                  TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateQsLink
                }
              >
                <Toggler.Enabled>
                  <va-accordion-item
                    header="Upload any file with QuickSubmit"
                    id="section-twenty"
                    level="3"
                  >
                    <p>
                      Check the QuickSubmit help page to learn how to upload
                      forms and files and complete eForms using QuickSubmit in
                      the Accredited Representative Portal.
                    </p>
                    <p>
                      <va-link
                        href="/representative/help/quicksubmit"
                        text="Get help using QuickSubmit in the portal"
                      />
                    </p>
                  </va-accordion-item>
                </Toggler.Enabled>
              </Toggler>
            </va-accordion>
            <div className="va-h-ruled--stars mobile-view-divider" />
          </div>
          <div className="vads-l-col--12 medium-screen:vads-l-col--4 contact-us">
            <va-accordion open-single>
              <va-accordion-item
                header="Contact Us"
                id="section-twenty-one"
                level="2"
                open="open"
              >
                <h3 className="no-spacing-top vads-u-font-size--h4">
                  VA accredited representative support line
                </h3>
                <p>
                  <va-telephone contact="8552250709" />
                  <br />
                  Hours: Monday through Friday, 8:00 a.m. to 9:00 p.m. ET
                </p>
                <p>
                  Call the VA accredited representative support line to get help
                  with these needs:
                </p>
                <ul>
                  <li>
                    Find out the status of a claim or appeal for disability
                    compensation, pension, or survivors benefits (including a
                    PACT Act claim)
                  </li>
                  <li>Resolve issues with sign-in to the portal</li>
                  <li>
                    Get answers to your other questions about the Accredited
                    Representative Portal
                  </li>
                </ul>
                <h3 className="vads-u-font-size--h4">Portal team</h3>
                <p>
                  Email the Accredited Representative Portal team at{' '}
                  {EmailHelpLink()}
                  if you’re a VSO manager or certifying official and you’d like
                  access to the Representation Request feature for your
                  organization.
                </p>
              </va-accordion-item>
            </va-accordion>
          </div>
        </div>
      </article>
    </>
  );
};

export default HelpPage;
